import * as THREE from 'three';

/**
 * Model animations as data. A model wraps its moving parts in `<Spin>`,
 * `<Pulse>`, `<Bob>` or `<Animated>` (`animated.tsx`), which tag the group with
 * `userData.anim`. Mounted directly (the gallery, the plain layers) the
 * wrapper drives its group from `animMatrix`; baked into instanced parts
 * (`batch.ts`) the same motion runs in the vertex shader (`ANIM_VERTEX_*`).
 * Both read the one description below, so the two never drift apart.
 *
 * The motion is about the wrapper's own origin: `T(bob + smoke) · R(yaw) ·
 * R(spin) · S(pulse · smoke)`.
 */
export type Anim = {
  /** Turn about an axis at `speed` rad/s (negative turns the other way). */
  spin?: { axis: 'x' | 'y' | 'z'; speed: number };
  /**
   * Rise and fall `amount` along y at `speed` rad/s, and sway `yaw` radians
   * about y at half that rate.
   */
  bob?: { amount: number; speed: number; phase?: number; yaw?: number };
  /**
   * Uniform scale `base + amount · sin(speed · t + phase)`; `rectify` keeps
   * only the positive half of the wave (a blink).
   */
  pulse?: { base?: number; amount: number; speed: number; phase?: number; rectify?: boolean };
  /**
   * A smoke puff's cycle: `speed` cycles per second from `offset`, rising
   * `rise` and drifting `drift` along x, swelling then shrinking away.
   */
  smoke?: { speed: number; offset: number; rise: number; drift: number };
};

const AXES = { x: 0, y: 1, z: 2 } as const;

const tmpT = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const tmpQ2 = new THREE.Quaternion();
const tmpS = new THREE.Vector3();
const AXIS_VECTORS = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, 0, 1),
];

/** The animated group's transform at time `t` (seconds), about its own origin. */
export function animMatrix(anim: Anim, t: number, out: THREE.Matrix4): THREE.Matrix4 {
  tmpT.set(0, 0, 0);
  tmpQ.identity();
  let scale = 1;
  if (anim.bob) {
    const { amount, speed, phase = 0, yaw = 0 } = anim.bob;
    const a = t * speed + phase;
    tmpT.y += amount * Math.sin(a);
    if (yaw) tmpQ.setFromAxisAngle(AXIS_VECTORS[1], yaw * Math.sin(a / 2));
  }
  if (anim.spin) {
    tmpQ2.setFromAxisAngle(AXIS_VECTORS[AXES[anim.spin.axis]], t * anim.spin.speed);
    tmpQ.multiply(tmpQ2);
  }
  if (anim.pulse) {
    const { base = 1, amount, speed, phase = 0, rectify = false } = anim.pulse;
    const s = Math.sin(t * speed + phase);
    scale *= base + amount * (rectify ? Math.max(0, s) : s);
  }
  if (anim.smoke) {
    const { speed, offset, rise, drift } = anim.smoke;
    const p = fract(t * speed + offset);
    tmpT.x += p * drift;
    tmpT.y += p * rise;
    scale *= Math.sin(p * Math.PI) * (0.7 + p);
  }
  return out.compose(tmpT, tmpQ, tmpS.setScalar(scale));
}

function fract(x: number): number {
  return x - Math.floor(x);
}

/** Floats per instance of the anim attributes (`aAnimA`…`aAnimD`, 4 each). */
export const ANIM_FLOATS = 16;

/**
 * Pack an anim (plus the entity's own time offset, so neighbours don't move in
 * step) into `ANIM_FLOATS` floats at `offset`, the layout the shader reads:
 * A = (spin axis or -1, spin speed, bob amount, bob speed),
 * B = (bob phase, yaw, pulse base, pulse amount),
 * C = (pulse speed, pulse phase, rectify, time offset),
 * D = (smoke speed, smoke offset, rise, drift).
 */
export function packAnim(anim: Anim, timeOffset: number, out: Float32Array, offset: number): void {
  const { spin, bob, pulse, smoke } = anim;
  out.set(
    [
      spin ? AXES[spin.axis] : -1,
      spin?.speed ?? 0,
      bob?.amount ?? 0,
      bob?.speed ?? 0,
      bob?.phase ?? 0,
      bob?.yaw ?? 0,
      pulse ? (pulse.base ?? 1) : 1,
      pulse?.amount ?? 0,
      pulse?.speed ?? 0,
      pulse?.phase ?? 0,
      pulse?.rectify ? 1 : 0,
      timeOffset,
      smoke?.speed ?? 0,
      smoke?.offset ?? 0,
      smoke?.rise ?? 0,
      smoke?.drift ?? 0,
    ],
    offset
  );
}

/** Vertex shader declarations for animated instanced parts: `animTransform` mirrors `animMatrix`. */
export const ANIM_VERTEX_PARS = /* glsl */ `
uniform float uAnimTime;
attribute vec4 aAnimA;
attribute vec4 aAnimB;
attribute vec4 aAnimC;
attribute vec4 aAnimD;
mat3 animRotation(int axis, float a) {
  float c = cos(a), s = sin(a);
  if (axis == 0) return mat3(1.0, 0.0, 0.0, 0.0, c, s, 0.0, -s, c);
  if (axis == 1) return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c);
  return mat3(c, s, 0.0, -s, c, 0.0, 0.0, 0.0, 1.0);
}
void animTransform(out mat3 rot, out float scale, out vec3 move) {
  float t = uAnimTime + aAnimC.w;
  move = vec3(0.0);
  rot = mat3(1.0);
  scale = 1.0;
  if (aAnimA.w != 0.0 || aAnimA.z != 0.0) {
    float a = t * aAnimA.w + aAnimB.x;
    move.y += aAnimA.z * sin(a);
    rot = animRotation(1, aAnimB.y * sin(a * 0.5));
  }
  if (aAnimA.x >= 0.0) rot = rot * animRotation(int(aAnimA.x + 0.5), t * aAnimA.y);
  float s = sin(t * aAnimC.x + aAnimC.y);
  scale *= aAnimB.z + aAnimB.w * (aAnimC.z > 0.5 ? max(0.0, s) : s);
  if (aAnimD.x != 0.0) {
    float p = fract(t * aAnimD.x + aAnimD.y);
    move += vec3(p * aAnimD.w, p * aAnimD.z, 0.0);
    scale *= sin(p * 3.141592653589793) * (0.7 + p);
  }
}
`;

/** Replaces three's `beginnormal_vertex` (not run by unlit materials, so each chunk derives the transform). */
export const ANIM_BEGIN_NORMAL = /* glsl */ `
mat3 animNormalRot;
float animNormalScale;
vec3 animNormalMove;
animTransform(animNormalRot, animNormalScale, animNormalMove);
vec3 objectNormal = animNormalRot * vec3(normal);
#ifdef USE_TANGENT
  vec3 objectTangent = animNormalRot * vec3(tangent.xyz);
#endif
`;

/** Replaces three's `begin_vertex`. */
export const ANIM_BEGIN_VERTEX = /* glsl */ `
mat3 animRot;
float animScale;
vec3 animMove;
animTransform(animRot, animScale, animMove);
vec3 transformed = animRot * (vec3(position) * animScale) + animMove;
#ifdef USE_ALPHAHASH
  vPosition = vec3(position);
#endif
`;
